import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { createPortal } from 'react-dom'
import {
  Award, Users, Globe, Target, CheckCircle, ArrowRight,
  Building, Zap, Recycle, Shield, Clock, Star,
  ChevronLeft, ChevronRight, MapPin, ArrowUpRight, X, FileText, Loader2
} from 'lucide-react'
import alKondaLogo from '../assets/al-konda-logo-optimized.webp'
import SEO from './SEO'

// ✅ Import images at the top for Vercel deployment
import backgroundImg from '../assets/AboutUsNew/background.avif'
import slider1 from '../assets/AboutUsNew/slider1.avif'
import slider2 from '../assets/AboutUsNew/slider2.avif'
import slider3 from '../assets/AboutUsNew/slider3.avif'
import timeline2011 from '../assets/AboutUsNew/2011.avif'
import timeline2014 from '../assets/AboutUsNew/2014.avif'
import timelineCurrent from '../assets/AboutUsNew/current.avif'
import cooPhoto from '../assets/coo_aras_kamal.jpg'

// --- Premium Card Component ---
const PremiumCard = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay }}
    className={`relative group overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 
    hover:border-white/20 transition-colors duration-500 rounded-2xl ${className}`}
  >
    <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.05] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
    <div className="relative z-10 h-full">
      {children}
    </div>
  </motion.div>
)

// --- Section Header Component ---
const SectionHeader = ({ subtitle, title, align = "center" }) => (
  <div className={`mb-16 ${align === "left" ? "text-left" : "text-center"}`}>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`flex items-center gap-3 mb-4 ${align === "center" ? "justify-center" : "justify-start"}`}
    >
      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
      <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase font-sans">
        {subtitle}
      </span>
      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
    </motion.div>
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className="text-4xl md:text-5xl lg:text-6xl font-light text-white font-display leading-tight"
    >
      {title}
    </motion.h2>
  </div>
)

// --- ADVANCED PREMIUM PDF MODAL COMPONENT (With Loading State) ---
const PremiumPdfModal = ({ pdfModal, setPdfModal, t }) => {
  if (!pdfModal.isOpen) return null;

  // State to track if PDF is loading
  const [isLoading, setIsLoading] = useState(true);

  const handleClose = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
    setIsLoading(true); // Reset loading state
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4 bg-black/90 backdrop-blur-md"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative w-full h-full sm:h-[90vh] sm:max-w-6xl bg-[#1a1a1a] sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Premium Header Bar */}
          <div className="flex items-center justify-between px-4 py-4 sm:px-6 bg-white/5 border-b border-white/10 backdrop-blur-xl z-10">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-blue-400" />
              </div>
              <div className="flex flex-col min-w-0">
                <h3 className="text-base sm:text-lg font-display font-medium text-white tracking-wide truncate">
                  {pdfModal.title}
                </h3>
                <span className="text-xs text-white/40 font-sans uppercase tracking-wider">{t('about.pdfModal.documentViewer')}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 ml-4">
              <a
                href={pdfModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-sm text-white/80 hover:text-white"
              >
                <ArrowUpRight size={16} />
                <span className="hidden sm:inline">{t('about.pdfModal.openNewTab')}</span>
              </a>

              <button
                onClick={handleClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center transition-all border border-white/5 hover:border-red-500/30 group"
                aria-label="Close"
              >
                <X size={20} className="text-white/80 group-hover:text-red-400 transition-colors" />
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 relative bg-[#0f0f0f] w-full">

            {/* Loading Spinner */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <Loader2 size={40} className="text-blue-500 animate-spin" />
              </div>
            )}

            {/* Mobile Fallback / Action */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 sm:hidden z-0">
              <FileText size={48} className="text-white/20 mb-4" />
              <p className="text-white/60 mb-6 max-w-xs">
                {t('about.pdfModal.mobileMessage')}
              </p>
              <a
                href={pdfModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors shadow-lg shadow-blue-900/20"
              >
                <span>{t('about.pdfModal.openDocument')}</span>
                <ArrowUpRight size={18} />
              </a>
            </div>

            {/* PDF Embed */}
            <object
              data={pdfModal.url}
              type="application/pdf"
              className="relative z-10 w-full h-full"
              onLoad={() => setIsLoading(false)}
            >
              <iframe
                src={pdfModal.url}
                className="w-full h-full"
                title={pdfModal.title}
                onLoad={() => setIsLoading(false)}
              />
            </object>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const AboutUsNew = () => {
  const { t } = useTranslation()
  const [currentTimelineIndex, setCurrentTimelineIndex] = useState(0)
  const [currentImageSlide, setCurrentImageSlide] = useState(0)
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' })
  const videoRef = useRef(null)

  const backgroundVideo = "https://player.vimeo.com/external/434045526.sd.mp4?s=c27eecc69a27dbc4ff2b87d38afc35f1a9e7c02d&profile_id=139"

  const imageSlides = [
    { center: slider1 },
    { center: slider2 },
    { center: slider3 }
  ]

  const stats = [
    { value: '25+', label: t('about.stats.yearsExperience'), icon: Clock },
    { value: '500+', label: t('about.stats.projectsCompleted'), icon: Building },
    { value: '12', label: t('about.stats.countriesServed'), icon: Globe },
    { value: '98%', label: t('about.stats.clientSatisfaction'), icon: Star }
  ]

  const services = [
    { title: t('header.servicesList.construction'), description: t('services.serviceDescriptions.construction'), icon: Building, link: "/services/construction" },
    { title: t('header.servicesList.powerGeneration'), description: t('services.serviceDescriptions.powerGeneration'), icon: Zap, link: "/services/power-generation" },
    { title: t('header.servicesList.environmental'), description: t('services.serviceDescriptions.environmental'), icon: Recycle, link: "/services/environmental" },
    { title: t('header.servicesList.oilFuelSupply'), description: t('services.serviceDescriptions.oilFuelSupply'), icon: Target, link: "/services/oil-fuel-supply" },
    { title: t('header.servicesList.logistics'), description: t('services.serviceDescriptions.logistics'), icon: Users, link: "/services/logistics" },
    { title: t('header.servicesList.generalTrading'), description: t('services.serviceDescriptions.generalTrading'), icon: Globe, link: "/services/general-trading" },
    { title: t('header.servicesList.recycling'), description: t('services.serviceDescriptions.recycling'), icon: Award, link: "/services/recycling" }
  ]

  const timeline = [
    {
      year: "2011",
      title: t('about.history.timeline.2011.title'),
      description: t('about.history.timeline.2011.description'),
      location: t('about.history.timeline.2011.location'),
      image: timeline2011
    },
    {
      year: "2014",
      title: t('about.history.timeline.2014.title'),
      description: t('about.history.timeline.2014.description'),
      location: t('about.history.timeline.2014.location'),
      image: timeline2014
    },
    {
      year: t('about.history.timeline.current.year'),
      title: t('about.history.timeline.current.title'),
      description: t('about.history.timeline.current.description'),
      location: t('about.history.timeline.current.location'),
      image: timelineCurrent
    }
  ]

  const values = [
    { icon: Target, title: t('about.values.excellence.title'), desc: t('about.values.excellence.desc') },
    { icon: Users, title: t('about.values.partnership.title'), desc: t('about.values.partnership.desc') },
    { icon: Shield, title: t('about.values.integrity.title'), desc: t('about.values.integrity.desc') },
    { icon: Award, title: t('about.values.innovation.title'), desc: t('about.values.innovation.desc') }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageSlide((prev) => (prev + 1) % imageSlides.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [imageSlides.length])

  return (
    <div className="relative w-full min-h-screen bg-black text-white selection:bg-blue-500/30 overflow-x-hidden">
      <SEO
        title="About AL-KONDA Group | Our Story, Mission & Values"
        description="Learn about AL-KONDA Group's 25+ years of excellence in construction, logistics, and environmental services across Iraq and the Middle East. Discover our mission, values, and commitment to sustainable development."
        keywords="about AL-KONDA, company history, mission, values, construction company Iraq, environmental services, corporate responsibility"
        url="/about"
      />

      <style dangerouslySetInnerHTML={{
        __html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Oswald:wght@200;300;400;500&display=swap');
        .font-display { font-family: 'Oswald', sans-serif; font-display: swap; }
        .font-sans { font-family: 'Inter', sans-serif; font-display: swap; }
        * { font-display: swap; -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
        html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
        body { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
      `}} />

      {/* --- Ambient Background --- */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0">
          <img
            src={backgroundImg}
            alt="Background"
            loading="lazy"
            className="w-full h-full object-cover opacity-60 blur-md"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/40 to-[#050505]/80" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />
      </div>

      {/* Main Content Wrapper */}
      <div className="relative z-10">

        {/* --- HERO SECTION --- */}
        <section className="relative min-h-screen flex items-center justify-center pt-20 pb-12 px-4 md:px-8">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12 w-full max-w-7xl mx-auto mt-16">
            <div className="grid lg:grid-cols-12 gap-12 items-center">

              <div className="lg:col-span-5 relative z-20">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1 }}
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-md">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    <span className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">{t('about.hero.est')}</span>
                  </div>

                  <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold mb-6 leading-tight tracking-tight">
                    {t('about.hero.title')} <br />
                    <span className="text-white">
                      {t('about.hero.titleHighlight')}
                    </span>
                  </h1>

                  <p className="text-xl md:text-2xl text-white/80 max-w-2xl font-light leading-relaxed mb-10">
                    {t('about.hero.description')}
                  </p>

                  {/* Mobile Image - Only visible on mobile */}
                  <div className="lg:hidden relative h-[300px] w-full flex items-center justify-center mb-10 rounded-[2rem]">
                    <AnimatePresence initial={false}>
                      <motion.div
                        key={currentImageSlide}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1 }}
                        className="absolute inset-0 rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-black/50"
                      >
                        <video
                          ref={videoRef}
                          className="w-full h-full object-cover opacity-80"
                          autoPlay loop muted playsInline
                          poster={imageSlides[currentImageSlide].center}
                        >
                          <source src={backgroundVideo} type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <a href="/services" className="group relative overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-white/20 transition-colors duration-500 rounded-2xl px-8 py-4 flex items-center gap-2">
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.05] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                    <span className="relative z-10 text-white font-medium">{t('about.hero.cta')}</span>
                    <ArrowRight className="relative z-10 w-5 h-5 text-white" />
                  </a>
                </motion.div>
              </div>

              {/* Desktop Image - Only visible on desktop */}
              <div className="lg:col-span-7 relative hidden lg:block">
                <div className="relative h-[300px] md:h-[500px] w-full flex items-center justify-center rounded-[2rem]">
                  <AnimatePresence initial={false}>
                    <motion.div
                      key={currentImageSlide}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1 }}
                      className="absolute inset-0 rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-black/50"
                    >
                      <video
                        ref={videoRef}
                        className="w-full h-full object-cover opacity-80"
                        autoPlay loop muted playsInline
                        poster={imageSlides[currentImageSlide].center}
                      >
                        <source src={backgroundVideo} type="video/mp4" />
                      </video>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- STATS STRIP --- */}
        <section className="relative border-y border-white/5 bg-white/[0.02] backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/5">
              {stats.map((stat, idx) => (
                <div key={idx} className="py-12 px-6 group hover:bg-white/[0.02] transition-colors relative cursor-default">
                  <div className="flex items-start justify-between mb-4">
                    <stat.icon className="w-6 h-6 text-blue-500/60 group-hover:text-blue-500 transition-colors" />
                    <ArrowUpRight className="w-4 h-4 text-white/20 group-hover:text-white/60 opacity-0 group-hover:opacity-100 transition-all" />
                  </div>
                  <div className="text-3xl xl:text-4xl font-display font-light text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs font-sans font-medium tracking-widest text-white/40 uppercase group-hover:text-white/60 transition-colors">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- OUR VISION --- */}
        <section className="py-32 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12">
            <SectionHeader subtitle={t('about.vision.subtitle')} title={t('about.vision.title')} />

            <div className="grid lg:grid-cols-3 gap-8 mt-16">
              <PremiumCard className="p-8" delay={0.1}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-blue-500/10 rounded-full flex items-center justify-center mb-6">
                    <Globe className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-display text-white mb-4">{t('about.vision.globalImpact.title')}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {t('about.vision.globalImpact.description')}
                  </p>
                </div>
              </PremiumCard>

              <PremiumCard className="p-8" delay={0.2}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                    <Recycle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-display text-white mb-4">{t('about.vision.sustainableFuture.title')}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {t('about.vision.sustainableFuture.description')}
                  </p>
                </div>
              </PremiumCard>

              <PremiumCard className="p-8" delay={0.3}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-purple-500/10 rounded-full flex items-center justify-center mb-6">
                    <Award className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="text-xl font-display text-white mb-4">{t('about.vision.excellenceDriven.title')}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {t('about.vision.excellenceDriven.description')}
                  </p>
                </div>
              </PremiumCard>
            </div>

            <div className="mt-16 text-center">
              <p className="text-xl text-white/80 max-w-4xl mx-auto font-light leading-relaxed">
                {t('about.vision.quote')}
              </p>
              <div className="mt-8 flex items-center justify-center gap-4">
                <div className="w-12 h-[1px] bg-blue-500/50" />
                <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase">{t('about.vision.vision2030')}</span>
                <div className="w-12 h-[1px] bg-blue-500/50" />
              </div>
            </div>
          </div>
        </section>

        {/* --- OUR MISSION --- */}
        <section className="py-32 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12">
            <SectionHeader subtitle={t('about.mission.subtitle')} title={t('about.mission.title')} />

            <div className="grid lg:grid-cols-2 gap-16 mt-16">
              <div className="space-y-8">
                <PremiumCard className="p-6" delay={0.1}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display text-white mb-2">{t('about.mission.infrastructure.title')}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {t('about.mission.infrastructure.description')}
                      </p>
                    </div>
                  </div>
                </PremiumCard>

                <PremiumCard className="p-6" delay={0.2}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display text-white mb-2">{t('about.mission.community.title')}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {t('about.mission.community.description')}
                      </p>
                    </div>
                  </div>
                </PremiumCard>

                <PremiumCard className="p-6" delay={0.3}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display text-white mb-2">{t('about.mission.safety.title')}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {t('about.mission.safety.description')}
                      </p>
                    </div>
                  </div>
                </PremiumCard>
              </div>

              <div className="flex flex-col justify-center">
                <PremiumCard className="p-8 text-center" delay={0.4}>
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-6">
                    <Target className="w-10 h-10 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-display text-white mb-4">{t('about.mission.coreMission.title')}</h3>
                  <p className="text-white/70 leading-relaxed mb-6">
                    {t('about.mission.coreMission.description')}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-blue-500/80">
                    <div className="w-2 h-2 rounded-full bg-blue-500/60" />
                    <span className="text-xs font-bold tracking-[0.2em] uppercase">{t('about.mission.coreMission.since')}</span>
                    <div className="w-2 h-2 rounded-full bg-blue-500/60" />
                  </div>
                </PremiumCard>
              </div>
            </div>
          </div>
        </section>

        {/* --- LEADERSHIP: COO SECTION --- */}
        <section className="py-32 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12">
            <SectionHeader subtitle={t('about.leadership.subtitle')} title={t('about.leadership.title')} />

            <div className="grid lg:grid-cols-2 gap-12 items-center mt-16">
              {/* Photo Column */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex justify-center lg:justify-start"
              >
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100" />
                  <div className="relative w-80 h-80 rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                    <img
                      src={cooPhoto}
                      alt="Mr. Aras Abdoka - Chief Operating Officer"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                </div>
              </motion.div>

              {/* Bio Column */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-3xl md:text-4xl font-display text-white mb-2">
                    {t('about.leadership.coo.name')}
                  </h3>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-[2px] w-12 bg-gradient-to-r from-blue-500 to-transparent" />
                    <span className="text-blue-500/80 text-sm font-bold tracking-[0.15em] uppercase">
                      {t('about.leadership.coo.title')}
                    </span>
                  </div>
                </div>

                <div className="space-y-4 text-white/70 leading-relaxed">
                  <p className="text-base">
                    {t('about.leadership.coo.bio.paragraph1')}
                  </p>
                  <p className="text-base">
                    {t('about.leadership.coo.bio.paragraph2')}
                  </p>
                  <p className="text-base">
                    {t('about.leadership.coo.bio.paragraph3')}
                  </p>
                </div>

                {/* Optional: Add a subtle accent */}
                <div className="pt-6 border-t border-white/5">
                  <div className="flex items-center gap-2 text-white/40">
                    <Users size={16} />
                    <span className="text-xs uppercase tracking-wider">
                      {t('about.leadership.coo.commitment')}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* --- SERVICES GRID --- */}
        <section className="py-16 sm:py-20 lg:py-32 relative overflow-hidden">

          <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 md:px-8">
            <div className="bg-white/[0.02] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12">
              <SectionHeader subtitle={t('about.expertise.subtitle')} title={t('about.expertise.title')} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {services.map((service, idx) => (
                  <a key={idx} href={service.link} className="block h-full">
                    <PremiumCard delay={idx * 0.1} className="group p-4 sm:p-6 lg:p-8 h-full flex flex-col justify-between hover:bg-white/[0.05] cursor-pointer">
                      <div>
                        <div className="w-12 h-12 bg-white/15 backdrop-blur-md rounded-lg border border-white/10 flex items-center justify-center mb-6 text-white group-hover:bg-white/25 transition-all duration-300">
                          <service.icon size={24} strokeWidth={1.5} />
                        </div>
                        <h3 className="text-xl font-display text-white mb-2">{service.title}</h3>
                        <div className="h-0.5 w-8 bg-white/10 group-hover:w-full group-hover:bg-blue-500/50 transition-all duration-500 mb-4" />
                        <p className="text-sm text-white/50 leading-relaxed font-light">{service.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-blue-500/60 group-hover:text-blue-500 transition-colors mt-4">
                        <span className="text-xs font-medium">{t('about.expertise.learnMore')}</span>
                        <ArrowRight size={14} />
                      </div>
                    </PremiumCard>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* --- TIMELINE JOURNEY --- */}
        <section className="py-16 sm:py-20 lg:py-32 px-3 sm:px-4 md:px-8 max-w-7xl mx-auto">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12">
            <SectionHeader subtitle={t('about.history.subtitle')} title={t('about.history.title')} />
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-24 items-center">
              <div className="relative h-[250px] sm:h-[300px] lg:h-[400px] w-full order-2 lg:order-1">
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={currentTimelineIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full rounded-2xl overflow-hidden border border-white/10 relative"
                  >
                    <img
                      src={timeline[currentTimelineIndex].image}
                      alt="Timeline"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />

                    <div className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-lg">
                      <div className="flex items-center gap-2 text-white">
                        <MapPin size={16} className="text-blue-500" />
                        <span className="font-display tracking-wider uppercase text-sm">{timeline[currentTimelineIndex].location}</span>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="order-1 lg:order-2 flex flex-col justify-center">
                <div className="flex gap-4 mb-8">
                  {timeline.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentTimelineIndex(idx)}
                      className={`h-1 transition-all duration-300 ${idx === currentTimelineIndex ? 'w-12 bg-blue-500' : 'w-4 bg-white/20 hover:bg-white/40'}`}
                    />
                  ))}
                </div>

                <AnimatePresence mode='wait'>
                  <motion.div
                    key={currentTimelineIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="text-3xl sm:text-4xl lg:text-5xl font-display text-blue-500 mb-3 sm:mb-4">{timeline[currentTimelineIndex].year}</h3>
                    <h4 className="text-xl sm:text-2xl lg:text-3xl text-white mb-4 sm:mb-6 font-light">{timeline[currentTimelineIndex].title}</h4>
                    <p className="text-sm sm:text-base lg:text-lg text-white/60 leading-relaxed font-light border-s-2 border-white/10 ps-4 sm:ps-6 mb-6 sm:mb-8">
                      {timeline[currentTimelineIndex].description}
                    </p>
                  </motion.div>
                </AnimatePresence>

                <div className="flex gap-3 sm:gap-4">
                  <button
                    onClick={() => setCurrentTimelineIndex((prev) => (prev - 1 + timeline.length) % timeline.length)}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all"
                  >
                    <ChevronLeft size={16} className="sm:w-5 sm:h-5 rtl:rotate-180" />
                  </button>
                  <button
                    onClick={() => setCurrentTimelineIndex((prev) => (prev + 1) % timeline.length)}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all"
                  >
                    <ChevronRight size={16} className="sm:w-5 sm:h-5 rtl:rotate-180" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- FOOTER --- */}
        <section className="relative pt-16 sm:pt-20 lg:pt-32 pb-8 sm:pb-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 mb-12 sm:mb-16 lg:mb-24">
            <div className="bg-white/[0.02] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12">
              <SectionHeader subtitle={t('about.values.subtitle')} title={t('about.values.title')} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                {values.map((val, idx) => (
                  <div key={idx} className="text-center group p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl hover:bg-white/[0.03] transition-colors">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 mx-auto bg-gradient-to-br from-white/10 to-transparent rounded-full flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 border border-white/5">
                      <val.icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white group-hover:text-blue-500 transition-colors" />
                    </div>
                    <h4 className="text-sm sm:text-base lg:text-lg font-display text-white mb-1 sm:mb-2">{val.title}</h4>
                    <p className="text-xs sm:text-sm text-white/40 leading-tight">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* --- COMPANY INFORMATION --- */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 mb-24">
            <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <SectionHeader align="left" subtitle={t('about.companyInfo.subtitle')} title={t('about.companyInfo.title')} />
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-white/[0.03] border border-white/5 rounded-lg">
                        <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-2">{t('about.companyInfo.duns')}</span>
                        <span className="text-white font-mono text-lg">559642862</span>
                      </div>
                      <div className="p-4 bg-white/[0.03] border border-white/5 rounded-lg">
                        <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-2">{t('about.companyInfo.ncage')}</span>
                        <span className="text-white font-mono text-lg">SBRXI</span>
                      </div>
                      <div className="p-4 bg-white/[0.03] border border-white/5 rounded-lg">
                        <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-2">{t('about.companyInfo.uei')}</span>
                        <span className="text-white font-mono text-lg">JEQ8KNPZ6JB5</span>
                      </div>
                      <div className="p-4 bg-white/[0.03] border border-white/5 rounded-lg">
                        <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-2">{t('about.companyInfo.krg')}</span>
                        <span className="text-white font-mono text-lg">19511</span>
                      </div>
                    </div>
                    <div className="mt-8 p-6 bg-white/[0.05] border border-white/10 rounded-xl">
                      <div className="space-y-3">
                        <div>
                          <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-1">{t('about.companyInfo.legalName')}</span>
                          <span className="text-white/90" dir="ltr">Thaer Slewa Alah Wrdi KONDA CO.</span>
                        </div>
                        <div>
                          <span className="text-blue-500/80 text-xs font-bold tracking-[0.2em] uppercase block mb-1">{t('about.companyInfo.dba')}</span>
                          <span className="text-white/90 font-medium">AL-KONDA</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <PremiumCard className="p-12 text-center max-w-md" delay={0.2}>
                    <div className="flex justify-center mb-8">
                      <div className="bg-white w-32 h-32 rounded-2xl overflow-hidden p-3 shadow-lg shadow-black/20">
                        <img
                          src={alKondaLogo}
                          alt="Al-Konda Logo"
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">
                      {t('about.companyInfo.description')}
                    </p>
                  </PremiumCard>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy & Commitment Section */}
          <div className="max-w-7xl mx-auto px-4 md:px-8" id="privacy-policy">
            <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-display text-white mb-4">{t('about.privacy.title')}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-6">
                    {t('about.privacy.description')}
                  </p>

                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { name: t('about.privacy.pdfs.codeOfEthics'), file: "code-of-ethics.pdf" },
                    { name: t('about.privacy.pdfs.sexualHarassment'), file: "sexual-harassment-policy.pdf" },
                    { name: t('about.privacy.pdfs.safetyHealth'), file: "safety-health-environmental-policy.pdf" },
                    { name: t('about.privacy.pdfs.maintenance'), file: "maintenance-policy.pdf" },
                    { name: t('about.privacy.pdfs.antiSlavery'), file: "anti-slavery-human-trafficking.pdf" },
                    { name: t('about.privacy.pdfs.antiBribery'), file: "anti-bribery-corruption.pdf" }
                  ].map((item, i) => (
                    <button key={i} onClick={() => setPdfModal({ isOpen: true, url: `/pdf/${item.file}`, title: item.name })} className="flex items-center gap-3 hover:text-blue-500 transition-colors cursor-pointer text-left">
                      <Shield size={14} className="text-white/30" />
                      <span className="text-sm text-white/60 hover:text-blue-500 transition-colors">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </section>

      </div>

      <PremiumPdfModal pdfModal={pdfModal} setPdfModal={setPdfModal} t={t} />
    </div>
  )
}

export default AboutUsNew